import {
  createContext,
  memo,
  MouseEvent,
  ReactNode,
  TouchEvent,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  WheelEvent,
} from 'react';

interface Transform {
  scale: number;
  translateX: number;
  translateY: number;
}

interface DragStart {
  x: number;
  y: number;
}

interface ZoomableContextType {
  transform: Transform;
  isDragging: boolean;
  centerOn: (x: number, y: number) => void;
  setScale: (scale: number) => void;
  resetTransform: () => void;
  handleWheel: (e: WheelEvent<HTMLDivElement>) => void;
  handleMouseDown: (e: MouseEvent<HTMLDivElement>) => void;
  handleMouseMove: (e: MouseEvent<HTMLDivElement>) => void;
  handleMouseUp: () => void;
  handleTouchStart: (e: TouchEvent<HTMLDivElement>) => void;
  handleTouchMove: (e: TouchEvent<HTMLDivElement>) => void;
  handleTouchEnd: () => void;
}

interface ZoomableProviderProps {
  children: ReactNode;
  minScale?: number;
  maxScale?: number;
  initialScale?: number;
  maxPanX?: number;
  maxPanY?: number;
}

const ZoomableContext = createContext<ZoomableContextType | undefined>(undefined);

export function ZoomableProvider({
  children,
  minScale = 0.5,
  maxScale = 4,
  initialScale = 1,
  maxPanX = 1000,
  maxPanY = 1000,
}: ZoomableProviderProps) {
  const [transform, setTransform] = useState<Transform>({
    scale: initialScale,
    translateX: 0,
    translateY: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<DragStart>({ x: 0, y: 0 });

  const clampPosition = useCallback(
    (x: number, y: number) => {
      return {
        x: Math.max(Math.min(x, maxPanX), -maxPanX),
        y: Math.max(Math.min(y, maxPanY), -maxPanY),
      };
    },
    [maxPanX, maxPanY],
  );

  const handleWheel = useCallback(
    (e: WheelEvent<HTMLDivElement>): void => {
      const scaleSensitivity = 0.002;
      setTransform((prev) => ({
        ...prev,
        scale: Math.min(Math.max(minScale, prev.scale - e.deltaY * scaleSensitivity), maxScale),
      }));
    },
    [maxScale, minScale],
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - transform.translateX,
        y: e.clientY - transform.translateY,
      });
    },
    [transform.translateX, transform.translateY],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>): void => {
      if (!isDragging) return;

      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      const clamped = clampPosition(newX, newY);

      setTransform((prev) => ({
        ...prev,
        translateX: clamped.x,
        translateY: clamped.y,
      }));
    },
    [isDragging, dragStart.x, dragStart.y, clampPosition],
  );

  const handleMouseUp = useCallback((): void => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback(
    (e: TouchEvent<HTMLDivElement>): void => {
      if (e.touches.length === 1) {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({
          x: e.touches[0].clientX - transform.translateX,
          y: e.touches[0].clientY - transform.translateY,
        });
      }
    },
    [transform.translateX, transform.translateY],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent<HTMLDivElement>): void => {
      if (!isDragging || e.touches.length !== 1) return;

      const newX = e.touches[0].clientX - dragStart.x;
      const newY = e.touches[0].clientY - dragStart.y;
      const clamped = clampPosition(newX, newY);

      setTransform((prev) => ({
        ...prev,
        translateX: clamped.x,
        translateY: clamped.y,
      }));
    },
    [isDragging, dragStart.x, dragStart.y, clampPosition],
  );

  const handleTouchEnd = useCallback((): void => {
    setIsDragging(false);
  }, []);

  const centerOn = useCallback(
    (x: number, y: number) => {
      const newX = -x + window.innerWidth / 2;
      const newY = -y + window.innerHeight / 2;
      const clamped = clampPosition(newX, newY);

      setTransform((prev) => ({
        ...prev,
        translateX: clamped.x,
        translateY: clamped.y,
      }));
    },
    [clampPosition],
  );

  const setScale = useCallback(
    (newScale: number) => {
      setTransform((prev) => ({
        ...prev,
        scale: Math.min(Math.max(minScale, newScale), maxScale),
      }));
    },
    [maxScale, minScale],
  );

  const resetTransform = useCallback(() => {
    setTransform({
      scale: initialScale,
      translateX: 0,
      translateY: 0,
    });
  }, [initialScale]);

  const value = useMemo(
    () => ({
      transform,
      isDragging,
      centerOn,
      setScale,
      resetTransform,
      handleWheel,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
    }),
    [
      transform,
      isDragging,
      centerOn,
      setScale,
      resetTransform,
      handleWheel,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
    ],
  );

  return <ZoomableContext.Provider value={value}>{children}</ZoomableContext.Provider>;
}

export function useZoomable() {
  const context = useContext(ZoomableContext);
  if (!context) {
    throw new Error('useZoomable must be used within ZoomableProvider');
  }
  return context;
}

interface SimpleZoomableProps {
  children: ReactNode;
  className?: string;
  minScale?: number;
  maxScale?: number;
  initialScale?: number;
}

function SimpleZoomableComponent({ children }: SimpleZoomableProps): JSX.Element {
  const {
    transform,
    isDragging,
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetTransform,
  } = useZoomable();

  const containerRef = useRef<HTMLDivElement>(null);

  const containerStyle = useMemo(
    () => ({
      cursor: isDragging ? 'grabbing' : 'grab',
      touchAction: 'manipulation',
      WebkitUserSelect: 'none' as const,
      userSelect: 'none' as const,
      WebkitTouchCallout: 'none' as const,
      WebkitTextSizeAdjust: 'none',
      width: '100%',
      height: '100%',
    }),
    [isDragging],
  );

  const contentStyle = useMemo(
    () => ({
      transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale})`,
      transformOrigin: 'center',
      transition: isDragging ? 'none' : 'transform 0.1s',
    }),
    [transform.translateX, transform.translateY, transform.scale, isDragging],
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      id="SimpleZoomable"
      ref={containerRef}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onDoubleClick={resetTransform}
      style={containerStyle}
    >
      <div style={contentStyle}>{children}</div>
    </div>
  );
}

export const SimpleZoomable = memo(SimpleZoomableComponent);
